import { useContext, useState } from 'react';
import { AppContext } from '../../contexts/AppContext';
import { Col, Row } from 'react-bootstrap';
import { IconButton } from '@velove/design-system';
import { useTranslation } from 'react-i18next';
import { getFloorName } from '../../utils/helpers';

const DeliveryInstructions = ({ isCustomerService }: { isCustomerService: boolean }) => {
  const { t } = useTranslation();
  const { trackingDetails, setCustomerInputsOpen, settings, partner } = useContext(AppContext);
  const [isNoteExpanded, setIsNoteExpanded] = useState(false);

  const truncate = (note: string) => {
    return note.length > 30 ? note.substring(0, 30 - 3) + '...' : note;
  };

  const generateFloorLabel = (floorNo: string) => {
    const floorNoString = String(floorNo).trim();

    // Extract the numeric part (including negative numbers)
    const numericMatch = floorNoString.match(/-?\d+/);
    const floorNumber = numericMatch ? Number(numericMatch[0]) : null;

    if (floorNumber === null) {
      return '-';
    }

    const floorName = getFloorName(floorNumber, settings);
    let direction = '';
    // Extract direction for Denmark only
    if (partner?.countryCode === 'dk') {
      // Match common Danish floor directions: MF, TV, TH.
      // Can be space-separated (e.g., "2 MF") or attached (e.g., "4TV")
      const directionMatch = floorNoString.match(/\d+\s*(MF|TV|TH)|^(MF|TV|TH)/i);
      if (directionMatch) {
        direction = (directionMatch[1] || directionMatch[2]).toUpperCase();
      }
    }

    return floorName + (direction ? ` ${direction}` : '');
  };

  return (
    <Row>
      <Col className="delivery_instruction_content">
        <Row>
          <Col xxs={7} sm={7} md={7}>
            <h5 className="delivery_instruction--label">{t('deliveryInstructions.floor')}</h5>
          </Col>
          <Col xxs={5} sm={5} md={5}>
            <h5 className="delivery_instruction--value">
              {trackingDetails?.recipient?.floorNo ? generateFloorLabel(trackingDetails?.recipient?.floorNo) : '-'}
            </h5>
          </Col>
          <Col xxs={12} sm={12} md={12}>
            <hr className="delivery_form--entry-separator" />
          </Col>
        </Row>
        {trackingDetails?.recipient?.nameOnDoor && (
          <Row>
            <Col xxs={7} sm={7} md={7}>
              <h5 className="delivery_instruction--label">{t('deliveryInstructions.name_on_door')}</h5>
            </Col>
            <Col xxs={5} sm={5} md={5}>
              <h5 className="delivery_instruction--value">{trackingDetails?.recipient?.nameOnDoor || '-'}</h5>
            </Col>
            <Col xxs={12} sm={12} md={12}>
              <hr className="delivery_form--entry-separator" />
            </Col>
          </Row>
        )}
        {trackingDetails?.recipient?.doorCode && (
          <Row>
            <Col xxs={7} sm={7} md={7}>
              <h5 className="delivery_instruction--label">{t('deliveryInstructions.door_code')}</h5>
            </Col>
            <Col xxs={5} sm={5} md={5}>
              <h5 className="delivery_instruction--value">{trackingDetails?.recipient?.doorCode || '-'}</h5>
            </Col>
            <Col xxs={12} sm={12} md={12}>
              <hr className="delivery_form--entry-separator" />
            </Col>
          </Row>
        )}
        {trackingDetails?.recipient?.intercom && (
          <Row>
            <Col xxs={7} sm={7} md={7}>
              <h5 className="delivery_instruction--label">{t('deliveryInstructions.intercom')}</h5>
            </Col>
            <Col xxs={5} sm={5} md={5}>
              <h5 className="delivery_instruction--value">{trackingDetails?.recipient?.intercom || '-'}</h5>
            </Col>
            <Col xxs={12} sm={12} md={12}>
              <hr className="delivery_form--entry-separator" />
            </Col>
          </Row>
        )}
        <Row>
          <Col xxs={4} sm={4} md={4}>
            <h5 className="delivery_instruction--label">{t('deliveryInstructions.note')}</h5>
          </Col>
          <Col xxs={8} sm={8} md={8}>
            <h5
              className="delivery_instruction--value"
              style={
                !isCustomerService && trackingDetails?.task?.taskInstructions && trackingDetails.task.taskInstructions.length > 30
                  ? {
                      cursor: 'pointer',
                      whiteSpace: isNoteExpanded ? 'normal' : 'nowrap',
                      wordBreak: 'break-word'
                    }
                  : undefined
              }
              onClick={() => {
                if (!isCustomerService && trackingDetails?.task?.taskInstructions && trackingDetails.task.taskInstructions.length > 30) {
                  setIsNoteExpanded((prev) => !prev);
                }
              }}
            >
              {isCustomerService || isNoteExpanded ? trackingDetails?.task?.taskInstructions || '-' : truncate(trackingDetails?.task?.taskInstructions || '-')}
            </h5>
          </Col>
          <Col xxs={12} sm={12} md={12}>
            <hr className="delivery_form--entry-separator" />
          </Col>
        </Row>
        <Row>
          {!isCustomerService && (
            <Col xxs={12} sm={12} md={12} align="right">
              <IconButton
                icon={'Edit'}
                sx={{
                  height: '40px',
                  width: '40px',
                  padding: '5px',
                  marginTop: 10,
                  color: 'black',
                  borderRadius: '100%',
                  borderStyle: 'solid',
                  borderWidth: '2px',
                  backgroundColor: 'white'
                }}
                onClick={() => setCustomerInputsOpen()}
              />
            </Col>
          )}
        </Row>
      </Col>
    </Row>
  );
};

export default DeliveryInstructions;
